import { useRef, useState, type ReactNode } from "react"
import { useLingui } from "@lingui/react/macro"
import {
  PortalPopover,
  PortalSheet,
  type Placement,
} from "@/components/v2/chrome/portal-popover"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { deletePromptCopy, type DeletePromptKind } from "@/lib/delete-prompt-copy"
import { DeleteConfirmPanel } from "@/components/v2/delete/delete-confirm-panel"

// The ONE delete affordance for notes and follow-ups across
// v2 (CON-253): a kebab that, on desktop, opens a portaled
// popover and, on mobile, a portaled bottom sheet. With no
// `extraActions` it opens straight into the confirm (a single
// destructive verb, like the inbox rail's note kebab); with
// `extraActions` it opens a small menu (e.g. Edit + Delete on
// the deal Next steps row) whose Delete item swaps the view
// to the same confirm. Heading + quoted subtitle + red button
// all come from the shared `deletePromptCopy`, so every
// surface reads identically. Hard delete; the caller's
// `onConfirmDelete` owns the request + any prop reload.

export interface DeleteKebabAction {
  key: string
  label: string
  icon?: ReactNode
  onSelect: () => void
  danger?: boolean
}

interface DeleteConfirmKebabProps {
  promptKind: DeletePromptKind
  quote?: string | null
  // Performs the delete. Resolve `true` on success (closes
  // the popover/sheet) or `false` on failure (keeps it open
  // so the rep can retry without re-opening the kebab).
  onConfirmDelete: () => Promise<boolean>
  // Actions rendered ABOVE Delete in a menu. When omitted /
  // empty the kebab opens straight into the confirm.
  extraActions?: DeleteKebabAction[]
  // Hover-reveal / sizing classes merged onto the kebab
  // button. The caller owns visibility (e.g.
  // `md:opacity-0 md:group-hover/row:opacity-100`).
  triggerClassName?: string
  ariaLabel?: string
  testId?: string
  // Desktop popover placement + stacking. Defaults clear the
  // slide-out drawer panel (`z-50`) so a row inside the
  // drawer still shows its confirm.
  placement?: Placement
  zClassName?: string
}

type View = "menu" | "confirm"

export function DeleteConfirmKebab({
  promptKind,
  quote,
  onConfirmDelete,
  extraActions = [],
  triggerClassName = "",
  ariaLabel = "More options",
  testId = "delete-kebab",
  placement = "bottom-end",
  zClassName = "z-[60]",
}: DeleteConfirmKebabProps) {
  const { t } = useLingui()
  const isMobile = useIsMobile()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>("menu")
  const [submitting, setSubmitting] = useState(false)

  const hasMenu = extraActions.length > 0
  const { heading, subtitle } = deletePromptCopy(promptKind, quote, t)

  const openMenu = () => {
    if (open) {
      setOpen(false)
      return
    }
    setView(hasMenu ? "menu" : "confirm")
    setOpen(true)
  }

  const close = () => {
    if (submitting) return
    setOpen(false)
  }

  const runDelete = () => {
    if (submitting) return
    setSubmitting(true)
    onConfirmDelete()
      .then((ok) => {
        if (ok) setOpen(false)
      })
      .catch(() => {
        // A rejected delete (a network throw or an unexpected
        // error in the caller) keeps the confirm open so the
        // rep can retry — `finally` clears `submitting` either
        // way so the buttons never get stuck disabled.
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const selectExtra = (action: DeleteKebabAction) => {
    setOpen(false)
    action.onSelect()
  }

  const content =
    view === "confirm" ? (
      <DeleteConfirmPanel
        heading={heading}
        subtitle={subtitle}
        isMobile={isMobile}
        submitting={submitting}
        onCancel={close}
        onDelete={runDelete}
      />
    ) : (
      <MenuView
        actions={extraActions}
        isMobile={isMobile}
        onSelectExtra={selectExtra}
        onSelectDelete={() => setView("confirm")}
      />
    )

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup={hasMenu ? "menu" : "dialog"}
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={openMenu}
        className={`flex shrink-0 items-center justify-center rounded-md size-6 text-[#9A8F7E] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${triggerClassName}`}
      >
        <KebabGlyph />
      </button>
      {isMobile ? (
        <PortalSheet
          open={open}
          onClose={close}
          ariaLabel={heading}
          zClassName={zClassName}
        >
          <div className="px-4 pt-1" data-testid="delete-kebab-sheet">
            {content}
          </div>
        </PortalSheet>
      ) : (
        <PortalPopover
          open={open}
          onClose={close}
          triggerRef={triggerRef}
          placement={placement}
          zClassName={zClassName}
          ariaLabel={heading}
          className="w-64 rounded-xl border border-solid border-[#E5DFD3] bg-white shadow-[0_8px_24px_rgba(30,27,23,0.18)] p-2"
        >
          {content}
        </PortalPopover>
      )}
    </>
  )
}

function MenuView({
  actions,
  isMobile,
  onSelectExtra,
  onSelectDelete,
}: {
  actions: DeleteKebabAction[]
  isMobile: boolean
  onSelectExtra: (action: DeleteKebabAction) => void
  onSelectDelete: () => void
}) {
  const rowClass = isMobile
    ? "flex items-center gap-3 w-full rounded-xl py-3 px-3 text-[15px] cursor-pointer"
    : "flex items-center gap-2.5 w-full rounded-lg py-2 px-2.5 text-[13px] cursor-pointer hover:bg-[#F7F5F0]"

  return (
    <div className="flex flex-col gap-0.5" data-testid="delete-kebab-menu">
      {actions.map((action) => (
        <button
          key={action.key}
          type="button"
          data-testid={`delete-kebab-action-${action.key}`}
          onClick={() => onSelectExtra(action)}
          className={`${rowClass} ${
            action.danger ? "text-[#C53030]" : "text-[#1E1B17]"
          }`}
        >
          {action.icon ? (
            <span className="flex shrink-0 items-center justify-center">
              {action.icon}
            </span>
          ) : null}
          <span className="font-medium">{action.label}</span>
        </button>
      ))}
      <button
        type="button"
        data-testid="delete-kebab-delete"
        onClick={onSelectDelete}
        className={`${
          isMobile
            ? "flex items-center gap-3 w-full rounded-xl py-3 px-3 text-[15px]"
            : "flex items-center gap-2.5 w-full rounded-lg py-2 px-2.5 text-[13px] hover:bg-[#FBF1F0]"
        } text-[#C53030] cursor-pointer`}
      >
        <TrashGlyph />
        <span className="font-medium">Delete</span>
      </button>
    </div>
  )
}

function KebabGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="5" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" />
    </svg>
  )
}

function TrashGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  )
}
